// Tenant calendar for query windows (SEN-395 / TZ-2).
// NomOS reads a window `[period_start, period_end)` as instants and buckets days on the tenant's local calendar
// (monad/period_binding.resolve_period: local midnights -> UTC instants). Sentinel's inclusive dates are local
// dates, so a date range maps to local midnights in the tenant's zone. The zone is read from the tenant
// settings, never hard-coded.
import { registerTenantCache, getConfig, nomosFetch } from './client';
import { setCountUnit, setTenantFiscalMonth, setTenantZone } from '@poiesis/locale';

const zoneCache = new Map<string, Promise<string>>();

/** The tenant's IANA zone (GET /tenants/{slug} -> settings.timezone); UTC when unset or not a string (as NomOS does). */
export function tenantZone(): Promise<string> {
  const cfg = getConfig();
  const key = `${cfg.baseUrl}|${cfg.tenant}`;
  let p = zoneCache.get(key);
  if (!p) {
    p = nomosFetch<{ settings?: { timezone?: unknown; fiscal_year_start_month?: unknown }; display?: { count_unit?: unknown } }>('GET', `/tenants/${encodeURIComponent(cfg.tenant)}`, undefined, { tenantScoped: false })
      .then((t) => {
        // SEN-433 T7: the same read carries the fiscal month, for Poiesis's fytd preset (one seam, both values)
        setTenantFiscalMonth(t.settings?.fiscal_year_start_month);
        // the unit a count is written in: served by the units registry, read here with the tenant
        setCountUnit(t.display?.count_unit);
        return typeof t.settings?.timezone === 'string' && t.settings.timezone ? t.settings.timezone : 'UTC';
      })
      // Poiesis components in Sentinel (the site pane) read the tenant's clock from their own seam
      .then((zone) => { setTenantZone(zone); return zone; });
    p.catch(() => zoneCache.delete(key));
    zoneCache.set(key, p);
  }
  return p;
}
export function clearTenantZone(): void { zoneCache.clear(); }
registerTenantCache(clearTenantZone);

/** Offset (ms, local minus UTC) of `zone` at the instant `at`. */
function offsetMs(zone: string, at: Date): number {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric' });
  const g: Record<string, number> = {};
  for (const p of f.formatToParts(at)) if (p.type !== 'literal') g[p.type] = Number(p.value);
  return Date.UTC(g.year, g.month - 1, g.day, g.hour, g.minute, g.second) - Math.floor(at.getTime() / 1000) * 1000;
}

/** Local midnight of calendar day `ymd` (YYYY-MM-DD) in `zone`, as a UTC instant (ISO, Z). */
export function localMidnight(ymd: string, zone: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d);
  let t = guess - offsetMs(zone, new Date(guess));
  t = guess - offsetMs(zone, new Date(t)); // re-evaluate across a DST edge
  return new Date(t).toISOString().replace('.000Z', 'Z');
}

/** The local calendar date (YYYY-MM-DD) of an instant in `zone`. */
export function localDate(at: Date, zone: string): string {
  return new Date(at.getTime() + offsetMs(zone, at)).toISOString().slice(0, 10);
}
